import {DateTime} from "luxon"

/**
 * Obtiene una fecha y hora a partir de una cadena ISO, una instancia Date de JavaScript o DateTime de Luxon.
 * @param {string | Date | DateTime} [value?] Cadena ISO, instancia Date de JavaScript o DateTime de Luxon. Si no se proporciona, se utiliza la fecha y hora actual.
 * @returns {DateTime} Instancia DateTime de Luxon.
 * @example
 * // Sin parámetros: Fecha y hora actual
 * getDateTime()
 * @example
 * // Desde cadena ISO: Soporta fechas cortas y completas
 * getDateTime("2026-01-01") // 2026-01-01T00:00:00.000
 * getDateTime("2026-01-01T12:34:56") // 2026-01-01T12:34:56.000
 * @example
 * // Desde instancia de objeto: Date de JavaScript y DateTime de Luxon
 * getDateTime(new Date())
 * getDateTime(DateTime.now())
 * @example
 * // Valor inválido: Retorna instancia de DateTime inválido
 * const invalidDateTime = getDateTime("invalid-date-time")
 * console.log(invalidDateTime.isValid) // false
 */
export function getDateTime(value) {
  if (!value) return DateTime.now()
  if (value instanceof DateTime) return value
  if (value instanceof Date) return DateTime.fromJSDate(value)
  return DateTime.fromISO(value)
}

/**
 * Obtiene una fecha y hora modificada a partir de una cadena ISO, una instancia Date de JavaScript o DateTime de Luxon.
 * @param {object} [options?] Opciones de modificación.
 * @param {string | Date | DateTime} [options.dateTime?] Cadena ISO, instancia Date de JavaScript o DateTime de Luxon. Si no se proporciona, se utiliza la fecha y hora actual.
 * @param {"local" | "utc" | "America/Mexico_City"} [options.zone?] Zona horaria para interpretar la fecha y hora. Si no se proporciona, se utiliza la zona horaria local.
 * @param {"year" | "month" | "week" | "day"} [options.startOf?] Si se proporciona, la fecha se ajusta al inicio del año, mes, semana o día.
 * @param {"year" | "month" | "week" | "day"} [options.endOf?] Si se proporciona, la fecha se ajusta al final del año, mes, semana o día.
 * @param {boolean} [options.atNoon?] Si es true, la hora se ajusta a las 12:00:00.000.
 * @returns {DateTime} Instancia DateTime de Luxon.
 * @example
 * // Zona horaria: Interpreta la fecha y hora en la zona horaria especificada
 * getModifiedDateTime({dateTime: "2026-01-01T12:34:56", zone: "utc"}) // 2026-01-01T12:34:56.000Z
 * getModifiedDateTime({dateTime: "2026-01-01T12:34:56", zone: "America/Mexico_City"}) // 2026-01-01T12:34:56.000-06:00
 * @example
 * // Inicio de unidad de tiempo: Ajusta la fecha al inicio del año, mes, semana o día
 * getModifiedDateTime({startOf: "day"}) // Fecha actual ajustada al inicio del día
 * getModifiedDateTime({dateTime: "2026-01-01T12:34:56", startOf: "day"}) // 2026-01-01T00:00:00.000
 * getModifiedDateTime({dateTime: "2026-01-08", startOf: "week"}) // 2026-01-05T00:00:00.000
 * getModifiedDateTime({dateTime: "2026-01-15", startOf: "month"}) // 2026-01-01T00:00:00.000
 * getModifiedDateTime({dateTime: "2026-06-20", startOf: "year"}) // 2026-01-01T00:00:00.000
 * @example
 * // Fin de unidad de tiempo: Ajusta la fecha al final del año, mes, semana o día
 * getModifiedDateTime({endOf: "day"}) // Fecha actual ajustada al final del día
 * getModifiedDateTime({dateTime: "2026-01-01T12:34:56", endOf: "day"}) // 2026-01-01T23:59:59.999
 * getModifiedDateTime({dateTime: "2026-01-08", endOf: "week"}) // 2026-01-11T23:59:59.999
 * getModifiedDateTime({dateTime: "2026-01-15", endOf: "month"}) // 2026-01-31T23:59:59.999
 * getModifiedDateTime({dateTime: "2026-06-20", endOf: "year"}) // 2026-12-31T23:59:59.999
 * @example
 * // Mediodía: Ignora la hora original y la establece a las 12:00:00.000
 * getModifiedDateTime({atNoon: true}) // Fecha actual con hora ajustada a las 12:00:00.000
 * getModifiedDateTime({dateTime: "2026-01-01", atNoon: true}) // 2026-01-01T12:00:00.000
 * getModifiedDateTime({dateTime: "2026-01-01T12:34:56", atNoon: true}) // 2026-01-01T12:00:00.000
 * getModifiedDateTime({dateTime: new Date(), atNoon: true}) // Date de JavaScript con hora ajustada a las 12:00:00.000
 * getModifiedDateTime({dateTime: DateTime.now(), atNoon: true}) // DateTime de Luxon con hora ajustada a las 12:00:00.000
 * @example
 * // Valor inválido: Retorna instancia de DateTime inválido
 * const invalidDateTime = getModifiedDateTime({dateTime: "invalid-date-time"})
 * console.log(invalidDateTime.isValid) // false
 */
export function getModifiedDateTime(options) {
  let dateTime = getDateTime(options?.dateTime)
  if (options?.zone) dateTime = dateTime.setZone(options.zone)
  if (options?.startOf) dateTime = dateTime.startOf(options.startOf)
  if (options?.endOf) dateTime = dateTime.endOf(options.endOf)
  if (options?.atNoon === true) dateTime = dateTime.set({hour: 12, minute: 0, second: 0, millisecond: 0})
  return dateTime
}

/**
 * Compara dos fechas y determina si representan el mismo día, ignorando la hora.
 * @param {string | Date | DateTime} dateTime1 Cadena ISO, instancia Date de JavaScript o DateTime de Luxon.
 * @param {string | Date | DateTime} dateTime2 Cadena ISO, instancia Date de JavaScript o DateTime de Luxon.
 * @returns {boolean} true si ambas fechas representan el mismo día, false en caso contrario o si alguna de las fechas es inválida.
 * @example
 * // Mismo día: Retorna true
 * isSameDay("2026-01-01", "2026-01-01T12:34:56") // true
 * isSameDay(new Date("2026-01-01"), new Date("2026-01-01T12:34:56")) // true
 * @example
 * // Diferente día: Retorna false
 * isSameDay("2026-01-01", "2026-01-02T12:34:56") // false
 * isSameDay(new Date("2026-01-01"), new Date("2026-01-02T12:34:56")) // false
 * @example
 * // Fecha inválida: Retorna false
 * isSameDay("invalid-date-time", "2026-01-01") // false
 * isSameDay("2026-01-01", "invalid-date-time") // false
 * isSameDay("invalid-date-time", "invalid-date-time") // false
 */
export function isSameDay(dateTime1, dateTime2) {
  dateTime1 = getDateTime(dateTime1)
  dateTime2 = getDateTime(dateTime2)
  return dateTime1.isValid && dateTime2.isValid && dateTime1.hasSame(dateTime2, "day")
}
